import { ReactElement } from "react";

import { FaCheck } from "react-icons/fa";

import { Link } from "@chakra-ui/next-js";
import { VStack, Box, Heading, Text, SimpleGrid, Flex, Icon } from "@chakra-ui/react";
import { Button, Container } from "@chakra-ui/react";

import { PlanEnum } from "@/__generated__/graphql";
import PricingCard from "@/components/Pricing/PricingCard";
import { coreFeatures } from "@/components/WorkspaceSubscription/planItems";
import PricingLayout from "@/layouts/PricingLayout";

const enterpriseFAQ = [
  {
    id: "pricing-structure",
    question: "How is Enterprise pricing structured?",
    answer:
      "Enterprise pricing is based on your specific needs and usage patterns. We'll work with you to create a custom plan that fits your requirements.",
  },
  {
    id: "what-will-it-cost",
    question: "Come on... what's it gonna cost?",
    answer:
      "We price to make sure it's more cost effective than you building and maintaining an environments platform on your own. Every organization and their technical needs are different, so we need to chat and understand your stack and team size. From there, we can give you a custom price tailored to your specific needs.",
  },
  {
    id: "cloud-providers",
    question: "What cloud providers are supported?",
    answer: "Release Enterprise works with AWS and GCP. Your workloads run in your own cloud account.",
  },
  {
    id: "support",
    question: "What kind of support is included?",
    answer:
      "Enterprise plans include 24/7 dedicated support, custom SLAs, and a dedicated Slack channel for your team.",
  },
];

function PricingPage() {
  return (
    <Container maxW="1200px" overflow="hidden" px={{ base: 3, md: 8 }}>
      <VStack py={{ base: 3, md: 4 }} spacing={{ base: 8, md: 12 }} w="full">
        {/* Core Features Section */}
        <Box bg="whiteAlpha.50" borderRadius="lg" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }} w="full">
          <VStack align="center" spacing={{ base: 4, md: 6 }}>
            <Heading color="white" size={{ base: "sm", md: "md" }} textAlign="center">
              Enterprise features for modern development teams
            </Heading>

            <SimpleGrid
              columns={{ base: 1, sm: 2, md: 3, lg: 4 }}
              maxW="1000px"
              spacing={{ base: 3, md: 4 }}
              w="full"
            >
              {coreFeatures.map(feature => (
                <Flex key={feature} align="center" color="gray.400" fontSize={{ base: "xs", md: "sm" }}>
                  <Icon as={FaCheck} boxSize={{ base: 2.5, md: 3 }} color="plum.500" mr={2} />
                  <Text>{feature}</Text>
                </Flex>
              ))}
            </SimpleGrid>
          </VStack>
        </Box>

        {/* Pricing Card */}
        <Flex justify="center" w="full">
          <Box maxW="600px" w="full">
            <PricingCard
              action={
                <Link isExternal href="https://release.com/book-a-demo" style={{ width: "100%" }}>
                  <Button colorScheme="plum" size="lg" width="full">
                    Schedule Meeting
                  </Button>
                </Link>
              }
              plan={PlanEnum.Enterprise}
            />
          </Box>
        </Flex>

        {/* FAQ Section */}
        <Box w="full">
          <VStack align="start" maxW="800px" mx="auto" spacing={{ base: 4, md: 6 }} w="full">
            <Heading color="plum.500" size={{ base: "sm", md: "md" }}>
              Frequently Asked Questions
            </Heading>
            {enterpriseFAQ.map(({ id, question, answer }) => (
              <Box key={id} w="full">
                <Text fontSize={{ base: "sm", md: "md" }} fontWeight="medium" mb={{ base: 1, md: 2 }}>
                  {question}
                </Text>
                <Box fontSize={{ base: "sm", md: "md" }}>{answer}</Box>
              </Box>
            ))}
          </VStack>
        </Box>
      </VStack>
    </Container>
  );
}

export default PricingPage;

PricingPage.getLayout = (page: ReactElement) => {
  return <PricingLayout>{page}</PricingLayout>;
};
